import {AbstractControl, ValidationErrors, AsyncValidatorFn, ValidatorFn, FormGroup, FormControl} from '@angular/forms';
import {fetchPswPolicy} from "./cognito";
import {REDWOOD_USER} from "./shared-worker-on-ui-thread";
import {tryGetTokenFromAmplifyState} from "./amplify/amplify_state";
import {graphqlRequest} from "./graphqlHelper";
import {PARAMS} from "./PARAMS";
import {generateUUID} from "../utils";

export function asyncPasswordValidator(): AsyncValidatorFn {
  let timeout: ReturnType<typeof setTimeout>; // Store timeout reference

  return (control: AbstractControl): Promise<ValidationErrors | null> => {
    return new Promise((resolve) => {
      // Clear any existing timeout to reset cooldown
      clearTimeout(timeout);

      // Set a cooldown period (e.g., 500ms)
      timeout = setTimeout(async () => {
        const value = control.value as string;
        if (!value) {
          resolve({ required: 'Wachtwoord is verplicht' });
          return;
        }

        try {
          const rules = await fetchPswPolicy(); // Fetch cached policy

          const minLength = new RegExp(`.{${rules.MinimumLength},}`);
          const hasLowerCase = rules.RequireLowercase ? /[a-z]/ : null;
          const hasNumber = rules.RequireNumbers ? /[0-9]/ : null;
          const hasSpecialChar = rules.RequireSymbols ? /[!@#$%^&*(),.?":{}|<>]/ : null;
          const hasUpperCase = rules.RequireUppercase ? /[A-Z]/ : null;

          const errors: ValidationErrors = {};

          if (!minLength.test(value)) {
            errors['minLength'] = `Wachtwoord moet minimaal ${rules.MinimumLength} tekens lang zijn`;
          }
          if (hasLowerCase && !hasLowerCase.test(value)) {
            errors['lowerCaseRequired'] = 'Wachtwoord moet minimaal 1 kleine letter bevatten';
          }
          if (hasNumber && !hasNumber.test(value)) {
            errors['numberRequired'] = 'Wachtwoord moet minimaal 1 cijfer bevatten';
          }
          if (hasSpecialChar && !hasSpecialChar.test(value)) {
            errors['specialCharRequired'] = 'Wachtwoord moet minimaal 1 speciaal teken bevatten';
          }
          if (hasUpperCase && !hasUpperCase.test(value)) {
            errors['upperCaseRequired'] = 'Wachtwoord moet minimaal 1 hoofdletter bevatten';
          }

          resolve(Object.keys(errors).length ? errors : null);
        } catch (error) {
          resolve({ policyFetchError: 'Kon het wachtwoordbeleid niet ophalen' });
        }
      }, 500); // Debounce time (500ms)
    });
  };
}
export function isUniqueValidator(): AsyncValidatorFn {
  let timeout: ReturnType<typeof setTimeout>; // Store timeout reference

  return (control: AbstractControl): Promise<ValidationErrors | null> => {
    return new Promise((resolve) => {
      // Clear any existing timeout to reset cooldown
      clearTimeout(timeout);

      // Set a cooldown period (e.g., 500ms)
      timeout = setTimeout(async () => {
        try {
          const formControls = ((control as FormGroup).parent);
          const allControls = (formControls && formControls['controls'] || []) as { [key: string]: AbstractControl }
          const controlName = formControls && Object.keys(allControls).find(
            (key) => allControls[key] === control
          );

          if (!controlName) throw new Error('Geen geldig control')

          const value = control.value

          const {errors: fetchedErrors, data} = await checkUniqueness(value, controlName)

          if (fetchedErrors.length) {
            throw new Error(`Kon ${controlName} niet valideren`)
          }

          const errors: ValidationErrors = {};

          if (!data.isUnique) {
            errors['notUnique'] = `${controlName} bestaat al in ons systeem`
          }


          resolve(Object.keys(errors).length ? errors : null);
        } catch (error) {
          resolve({ uniqueFetchError: error });
        }
      }, 500); // Debounce time (500ms)
    });
  };
}


export function passwordMatchValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formControls = ((control as FormGroup).parent);

    const password = formControls?.get('password')?.value;
    const pwConfirm = formControls?.get('pwConfirm')?.value;

    if (password !== pwConfirm) {
      return { passwordMismatch: 'Wachtwoorden komen niet overeen' }; // Error key and message
    }

    return null; // No errors
  };
}
export function regimentalNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    const role = control?.parent?.value['school_role']

    if (!value) {
      return null;
    }

    if (role && role !== 'ROLE_TEACHER') {
      return null
    }

    if (!isValidRegimentalNumber(value)) {
      return { regimentalNumber: 'Stamboeknummer is niet geldig' }; // Error key and message
    }

    return null; // No errors
  };
}

export function isValidRegimentalNumber(regimentalNumber: string) {
  if (!regimentalNumber) {
    return false
  }

  // gender 1 or 2.
  const gender = parseInt(regimentalNumber[0], 10);
  if (gender !== 1 && gender !== 2) {
    return false
  }

  // month between 1 and 12
  const month = parseInt(regimentalNumber.slice(3, 5), 10);
  if (month < 1 || month > 12) {
    return false
  }

  // day between 1 and 31
  const day = parseInt(regimentalNumber.slice(5, 7), 10);
  if (day < 1 || day > 31) {
    return false
  }

  // validate check number
  const mainNumber = parseInt(regimentalNumber.slice(0, 9), 10)
  let check = parseInt(regimentalNumber.slice(9, 11), 10)
  check = (check === 97) ? 0 : check
  const calculatedCheck = mainNumber % 97

  return check === calculatedCheck
}

async function checkUniqueness(input: string, controlName: string):Promise<{ data: any, errors: Error[] }> {
  const email = controlName === 'emailadres' ? input : generateUUID();
  const username = controlName !== 'emailadres' ? input : generateUUID();
  const uniqueMap = {
    'gebruikersnaam' : {dataProp: 'usernameExists'},
    'emailadres': {dataProp: 'emailExists'}
  }
  const key = controlName as keyof typeof uniqueMap;
  const dataProp = uniqueMap[key]?.dataProp || '';

  const result : { data: any, errors: Error[] } = { data: undefined, errors: [] }
  const token = tryGetTokenFromAmplifyState(localStorage)
    if (!token) throw new Error('user not authenticated')
    const query = `
      query checkUniqueEmailUsernameQuery($username: String!, $email: String!) {
            checkUniqueEmailUsernameQuery(username: $username, email: $email) {
                ${dataProp || ''}
            }
        }`
  const variables = { email: '' + email, username: '' + username };

  const operationName = 'checkUniqueEmailUsernameQuery'

  const {data, errors} = await graphqlRequest( {
    url: PARAMS["REDWOOD-LAMBDA"]+'/query',
    query,
    operationName,
    token,
    variables
  })

  if (errors.length) {
     result.errors = errors
  }
  result.data = {isUnique : !data?.data?.checkUniqueEmailUsernameQuery[dataProp]}

  return result
}
